// E8RemainderChoiceClosedTask.js
// Zadanie zamkniete E8: wskazanie liczby dajacej podana reszte.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import {
  analyzeWrittenDivision,
  LongDivisionBoard,
} from "../../temat8_dzialania_pisemne/podtemat4_dzielenie_pisemne_types/NaturalWrittenDivisionTask";

const LETTERS = ["A", "B", "C", "D"];

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function createOriginalTask() {
  const options = [91, 92, 95, 97].map((value, index) => ({
    label: LETTERS[index],
    value,
  }));

  return {
    type: "E8RemainderChoiceClosedTask",
    data: {
      divisor: 7,
      remainder: 1,
      quotient: 13,
      correctNumber: 92,
      options,
      correctIndex: 1,
    },
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 3,
    },
    tags: ["e8", "podzielnosc", "dzielenie_z_reszta"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  for (let attempt = 0; attempt < 200; attempt += 1) {
    const divisor = pick([4, 5, 6, 7, 8, 9]);
    const firstNumber = pick(
      Array.from({ length: 57 }, (_, index) => index + 40)
    );
    const values = [0, 1, 2, 3].map((offset) => firstNumber + offset);
    const correctIndex = pick([0, 1, 2, 3]);
    const correctNumber = values[correctIndex];
    const remainder = correctNumber % divisor;
    const quotient = Math.floor(correctNumber / divisor);
    const options = values.map((value, index) => ({
      label: LETTERS[index],
      value,
    }));

    return {
      type: "E8RemainderChoiceClosedTask",
      data: {
        divisor,
        remainder,
        quotient,
        correctNumber,
        options,
        correctIndex,
      },
      source: { variant: "random", basedOn: "E8_2025_MAJ_3" },
      tags: ["e8", "podzielnosc", "dzielenie_z_reszta"],
    };
  }

  return {
    ...createOriginalTask(),
    source: { variant: "random", basedOn: "E8_2025_MAJ_3" },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Dane są liczby:{" "}
        <InlineMath math={data.options.map((option) => option.value).join(",\\;")} />.
      </p>
      <p>
        Która z podanych liczb przy dzieleniu przez{" "}
        <InlineMath math={`${data.divisor}`} /> daje resztę{" "}
        <InlineMath math={`${data.remainder}`} />? Wybierz właściwą odpowiedź
        spośród podanych.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "8px",
        }}
      >
        {data.options.map((option) => (
          <div key={option.label}>
            <ChoiceOptionMath letter={option.label} math={`${option.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        value: index,
        label: option.label,
        textNode: option.label,
      }))}
      value={choiceIndex}
      onChange={(index) => {
        const option = data.options[index];
        onChange({
          choiceIndex: index,
          label: option?.label,
          value: option?.value,
        });
      }}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const option = data.options[data.correctIndex];
  return <ChoiceOptionMath letter={option.label} math={`${option.value}`} />;
}

export function renderUserAnswer(userAnswer, task) {
  const option = task?.data?.options?.[userAnswer?.choiceIndex];
  return option ? (
    <ChoiceOptionMath letter={option.label} math={`${option.value}`} />
  ) : (
    <i>(brak odpowiedzi)</i>
  );
}

export function renderExplanation({ data }) {
  const firstNumber = data.options[0].value;
  const firstDivision = analyzeWrittenDivision(firstNumber, data.divisor);
  const finalBoard =
    firstDivision.steps[firstDivision.steps.length - 1];
  const progression = data.options
    .map((option) => {
      const quotient = Math.floor(option.value / data.divisor);
      const remainder = option.value % data.divisor;
      return `${option.value}=${data.divisor}\\cdot${quotient}+${remainder}`;
    })
    .join(",\\qquad ");
  const correctOption = data.options[data.correctIndex];

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Dzielimy pisemnie pierwszą z podanych liczb,
        czyli <InlineMath math={`${firstNumber}:${data.divisor}`} />.
      </p>
      <div style={{ display: "flex", justifyContent: "center", margin: "12px 0 18px" }}>
        <LongDivisionBoard step={finalBoard} />
      </div>
      <p>
        Otrzymujemy iloraz <InlineMath math={`${firstDivision.quotient}`} /> i
        resztę <InlineMath math={`${firstDivision.remainder}`} />.
      </p>
      <p>
        <strong>Krok 2.</strong> Gdy zwiększamy dzielną o{" "}
        <InlineMath math="1" />, reszta również zwiększa się o{" "}
        <InlineMath math="1" />. Po reszcie{" "}
        <InlineMath math={`${data.divisor - 1}`} /> pojawia się reszta{" "}
        <InlineMath math="0" />, a iloraz zwiększa się o{" "}
        <InlineMath math="1" />. Jeśli następna podana liczba jest większa o
        kilka, przesuwamy się w tym cyklu o tyle samo kroków.
      </p>
      <BlockMath math={progression} />
      <p>
        <strong>Krok 3.</strong> Szukanej reszcie{" "}
        <InlineMath math={`${data.remainder}`} /> odpowiada liczba{" "}
        <InlineMath math={`${data.correctNumber}`} />.
      </p>
      <BlockMath
        math={`${data.correctNumber}=${data.divisor}\\cdot${data.quotient}+${data.remainder}`}
      />
      <p>
        <strong>Krok 4.</strong> Wybieramy odpowiedź{" "}
        <strong>
          <InlineMath math={`${correctOption.label}.\\;${correctOption.value}`} />
        </strong>
        .
      </p>
    </div>
  );
}
